import AvTimerRoundedIcon from '@mui/icons-material/AvTimerRounded';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import {
  Autocomplete,
  AutocompleteRenderGetTagProps,
  AutocompleteRenderInputParams,
  Box,
  Chip,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { SyntheticEvent, useMemo, useState } from 'react';
import { UUID } from 'v2/core/entities/uuid';

interface AddPhraseProps {
  feedbackID: UUID;
  labelID?: UUID;
  phraseList: string[];
  setPhraseList: React.Dispatch<React.SetStateAction<string[]>>;
}

export const AddPhrase = ({
  feedbackID,
  labelID,
  phraseList,
  setPhraseList,
}: AddPhraseProps) => {
  const [phrase, setPhrase] = useState('');

  const options = useMemo(
    () => (phrase.trim() ? [phrase.trim()] : []),
    [phrase],
  );

  const onPhraseChange = (
    _: SyntheticEvent<Element, Event>,
    phrases: string[],
  ) => {
    if (!phrase.trim() || options.length === 0) {
      setPhrase(phrase.trim());
      return;
    }

    setPhraseList(phrases);
    setPhrase('');
  };

  const getAutoCompleteLoader = () => (
    <Stack direction="row" spacing={1} alignItems="center">
      <AvTimerRoundedIcon
        sx={{
          width: '14px',
          height: '14px',
          color: (theme) => theme.palette.warning.dark,
        }}
      />
      <Typography
        sx={{
          color: (theme) => theme.palette.warning.dark,
        }}
        variant="caption"
      >
        Searching feedback record for matches...
      </Typography>
    </Stack>
  );

  const renderAutocompleteTags = (
    value: string[],
    getTagProps: AutocompleteRenderGetTagProps,
  ) =>
    value.map((option: string, index: number) => (
      <Chip
        variant="outlined"
        label={option}
        {...getTagProps({ index })}
        onDelete={() =>
          setPhraseList((prev) => prev.filter((_, i) => i !== index))
        }
      />
    ));

  const renderAutocompleteInput = (params: AutocompleteRenderInputParams) => (
    <TextField
      {...params}
      autoFocus
      fullWidth
      size="small"
      placeholder="Enter Phrases"
    />
  );

  const renderAutocompleteOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: string,
  ) => {
    return (
      <Stack
        direction="row"
        spacing={1}
        component="li"
        {...props}
        alignItems="center"
      >
        <Stack spacing={1}>
          <Typography display="flex" variant="body2" color="text.secondary">
            Add&nbsp;
            <Stack component="span" color="text.primary">
              "{option}"
            </Stack>
            &nbsp;as a phrase
          </Typography>
        </Stack>
      </Stack>
    );
  };

  return (
    <Stack spacing={0.5}>
      <Typography>
        Add Phrase{' '}
        {labelID && (
          <Box component="span" color="text.secondary">
            (Optional)
          </Box>
        )}
      </Typography>
      <Autocomplete
        multiple
        loadingText={getAutoCompleteLoader()}
        disablePortal
        value={phraseList}
        inputValue={phrase}
        clearIcon={
          <ClearRoundedIcon
            fontSize="small"
            onClick={() => setPhraseList([])}
          />
        }
        options={options}
        freeSolo
        renderTags={renderAutocompleteTags}
        onChange={onPhraseChange}
        onInputChange={(_, value) => {
          setPhrase(value);
        }}
        renderOption={renderAutocompleteOption}
        renderInput={renderAutocompleteInput}
      />
    </Stack>
  );
};
